import { HostApplicationType } from "@prisma/client";
import Link from "next/link";
import { CustomizableLayout } from "~/types/CustomizableLayout";
import { cn } from "~/utils/fontsConfig";
import { slugifyUser } from "~/utils/string";
import { If } from "../Conditional";
import { Button } from "../themed/Button";
import Modal from "../themed/Modal";
import ThemedLink from "../themed/ThemedLink";
import { ApplyToHostStepsPipelineBuilderData } from "./v2/actions/ApplyToHostStepsPipelineBuilder";
import { useWhiteLabel } from "~/app/providers";

type ApplyToHostModalProps = {
    type: HostApplicationType;
    submit: () => void;
    setOpen: (show: boolean) => void;
    open: boolean;
    isPreapprovedHost: boolean;
    user?: ApplyToHostStepsPipelineBuilderData["data"]["user"];
};

const ApplyToHostModal = ({
    open,
    setOpen,
    submit,
    isPreapprovedHost,
    user,
}: ApplyToHostModalProps) => {
    const customization = useWhiteLabel()?.customization;
    const whiteProps = useWhiteLabel()?.whiteLabelProps;

    return (
        <Modal
            open={open}
            onOpenChange={setOpen}
            title={isPreapprovedHost ? "Claim Host" : "Apply to Host"}
            description="What's involved:"
            customization={customization}
            footer={
                <div className="flex flex-row items-center justify-end gap-2">
                    <Button
                        className={cn(
                            "w-[33%]",
                            customization?.buttons?.default,
                        )}
                        onClick={submit}
                    >
                        <If condition={!isPreapprovedHost}>Apply to host</If>
                        <If condition={isPreapprovedHost}>Claim</If>
                    </Button>
                    <Button
                        className={cn(customization?.buttons?.destructive)}
                        variant="secondary"
                        onClick={() => {
                            setOpen(false);
                        }}
                    >
                        Nevermind!
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col justify-start text-start">
                <div
                    className={cn(
                        "mt-2 text-left font-mono text-[16px] font-normal leading-normal text-white",
                        customization?.descriptionClassName,
                    )}
                >
                    <ul>
                        <li>• Find a venue</li>
                        <li>• Set the capacity</li>
                        <li>• Set start & end time</li>
                        <li>• Welcome & check-in attendees</li>
                    </ul>
                </div>
                <If condition={!isPreapprovedHost}>
                    <div
                        className={cn(
                            "mt-4 text-left font-mono text-[12px] font-normal leading-normal",
                            customization?.descriptionClassName,
                        )}
                    >
                        We choose hosts based on your{"  "}
                        <Link
                            href={`/profile/${slugifyUser({
                                name: user?.name,
                                id: user?.id,
                                slug: user?.slug,
                            })}`}
                            className={cn(customization?.linkClassName)}
                        >
                            River Profile
                        </Link>{" "}
                        and your past engagement in the community.
                    </div>
                </If>

                <p className={cn("mt-4", customization?.descriptionClassName)}>
                    <ThemedLink
                        href={
                            whiteProps?.hostingLink ??
                            "https://www.getriver.io/articles/the-art-of-hosting"
                        }
                        className={cn("mb-4", customization?.linkClassName)}
                    >
                        See our full hosting guide
                    </ThemedLink>
                </p>
            </div>
        </Modal>
    );
};

export default ApplyToHostModal;
